import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button';
import LoadState from 'src/components/LoadState/LoadState';
import Callout from 'src/components/InformationCallout/Callout';
import NewBadge from 'src/components/NewBadge/NewBadge';
import { variables } from 'src/Tenants';
import SuiteManagerCard from 'src/pages/Property/ManageStrataManagers/components/SuiteManagerCard/SuiteManagerCard';
import ManageStrataManagersSkeleton from 'src/pages/Property/ManageStrataManagers/components/ManageStrataManagersSkeleton';
import {
  useStrataAssignmentsData,
  UseStrataAssignmentsDataReturn,
} from 'src/pages/Property/ManageStrataManagers/CustomHooks/useStrataAssignmentsData';
import {
  IStrataManager,
  IUnitStrataAssignments,
} from 'src/pages/Property/ManageStrataManagers/types';
import './manage-strata-managers-dialog.scss';

interface ManageStrataManagersDialogProps {
  propertyId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface StrataColumnProps {
  units: IUnitStrataAssignments[];
  availableManagers: IStrataManager[];
  emptyLabel: string;
}

const StrataColumn = ({
  units,
  availableManagers,
  emptyLabel,
}: StrataColumnProps) => {
  if (units.length === 0) {
    return <div className="manage-strata-managers-dialog-empty">{emptyLabel}</div>;
  }

  return (
    <div className="manage-strata-managers-dialog-list">
      {units.map((unit: IUnitStrataAssignments) => (
        <SuiteManagerCard
          key={unit.unitId}
          unit={unit}
          availableManagers={availableManagers}
        />
      ))}
    </div>
  );
};

const ManageStrataManagersDialog = ({
  propertyId,
  isOpen,
  onClose,
}: ManageStrataManagersDialogProps) => {
  const {
    commonAreas,
    generalUnits,
    availableManagers,
    isLoading,
    error,
  }: UseStrataAssignmentsDataReturn = useStrataAssignmentsData(propertyId);

  const isEmpty: boolean = commonAreas.length === 0 && generalUnits.length === 0;

  const footer = (
    <div className="manage-strata-managers-dialog-footer">
      <Button color="teal" size="small" onClick={() => onClose()}>
        Done
      </Button>
    </div>
  );

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          Manage {variables.strataManagers}
          <NewBadge spaced />
        </>
      }
      description={`Assign ${variables.strataManagers} to common areas on this property — a common area can have more than one.`}
      maxWidth="130rem"
      footer={footer}
    >
      <div className="manage-strata-managers-dialog">
        {isLoading ? (
          <ManageStrataManagersSkeleton />
        ) : (
        <LoadState
          isLoading={false}
          isEmpty={isEmpty}
          error={error}
          emptyNode={<div>No units found for this property.</div>}
        >
          <div className="manage-strata-managers-dialog-columns">
            <section className="manage-strata-managers-dialog-column">
              <Callout
                variant="info"
                title="Common areas"
                subtitle={`We recommend assigning ${variables.strataManagers} to common areas — that's typically where they manage shared responsibilities.`}
              />
              <StrataColumn
                units={commonAreas}
                availableManagers={availableManagers}
                emptyLabel="No common areas on this property."
              />
            </section>

            <div className="manage-strata-managers-dialog-divider" />

            <section className="manage-strata-managers-dialog-column">
              <Callout
                variant="info"
                title="Individual units"
                subtitle={`You can also assign ${variables.strataManagers} to individual units if you need to.`}
              />
              <StrataColumn
                units={generalUnits}
                availableManagers={availableManagers}
                emptyLabel="No other units on this property."
              />
            </section>
          </div>
        </LoadState>
        )}
      </div>
    </GenericDialogue>
  );
};

export default ManageStrataManagersDialog;